// Join.jsx
'use strict';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import ModalView from '../container/ModalView.js';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import Loading from '../component/Loading.jsx';

import useCaptchaForm from '../hook/useCaptchaForm.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import {
  iPhoneXSeriesFooterPaddingInPWA,
  color,
  textColor,
  headerHeight,
} from '../style/variables.js';

import { ButtonId, ModalId } from '../resource/trackingConstants.js';
import { Progress as JoinProgress } from '../resource/joinConstants.js';
import {
  getIsIphoneXSeriesPWA,
  getIsXUserAgent,
} from '../resource/getUserAgent.js';
import {
  getIsInIframe,
  getIsInBrowserMainThread,
} from '../resource/getJsEnvironment.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

import { KYC_FAILED, KYC_STARTED } from '../serviceWorker/PusherEvents.js';

import ShuftiLogoSource from '../../img/img_shufti_logo.svg';
import CloseIconSource from '../../img/ic_topbar_close.svg';

const SUBMIT_BUTTON_HEIGHT = 40;

const componentZIndex = {
  header: 1,
  progress: 1,
};

const isClient = getIsInBrowserMainThread();
const isXUserAgent = isClient ? getIsXUserAgent() : false;
const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();
const isInIframe = getIsInIframe();

const CaptchaWrapper = ({ onSubmit, children }) => {
  const { startVerification, CaptchaWidget, isWaitingForCaptcha } =
    useCaptchaForm({
      onVerificationSuccess: onSubmit,
    });
  return children({ startVerification, CaptchaWidget, isWaitingForCaptcha });
};

const Join = React.memo(function Join({
  meId = null,
  progress = null,
  kycStatus = null,
  deviceHeight = 0,
  canSubmit = false,
  isSubmitMeKycFetching = false,
  isSubmitMeCreatorFetching = false,
  isFetchMeCreatorFetching = false,
  isSubmitPasswordLoginFetching = false,
  isMeAccountLinksFetching = false,
  isEmailBindFetching = false,
  isEmailBindOtpFetching = false,
  addModal = NULL_FUNCTION,
  proceedJoinStep = NULL_FUNCTION,
  fetchMeCreator = NULL_FUNCTION,
  goBack = NULL_FUNCTION,
}) {
  const { t } = useTranslation();
  const [isServer, setIsServer] = useState(true);
  const nextTick = useRef(null);
  const startCaptchaVerification = useRef(null);
  const fetchMeCreatorRef = useRef(fetchMeCreator);
  const previousMeId = useRef(meId);

  fetchMeCreatorRef.current = fetchMeCreator;

  const handleClickSubmit = () => {
    if (JoinProgress.PERSONAL_INFORMATION === progress) {
      startCaptchaVerification.current?.();
      return;
    }
    return proceedJoinStep({ progress });
  };
  const handleClickClose = () => {
    if (
      [JoinProgress.OCR_PROCESSING, JoinProgress.MANUAL_REVIEW].includes(
        progress
      )
    ) {
      return goBack();
    }
    return addModal({
      id: 'LeaveJoinConfirmModal',
    });
  };
  const renderSubmitButton = () => {
    const isReverifyButton =
      JoinProgress.OCR_PROCESSING === progress && KYC_FAILED === kycStatus;
    const isGotItButton =
      JoinProgress.MANUAL_REVIEW === progress ||
      (JoinProgress.OCR_PROCESSING === progress && KYC_STARTED === kycStatus);
    const isFinishButton = JoinProgress.OCR_RESULT === progress;
    if (isServer || (isGotItButton && isXUserAgent)) {
      return null;
    }
    const isLoading =
      isSubmitMeKycFetching ||
      isSubmitMeCreatorFetching ||
      isFetchMeCreatorFetching ||
      isSubmitPasswordLoginFetching ||
      isMeAccountLinksFetching ||
      isEmailBindFetching ||
      isEmailBindOtpFetching;

    return (
      <SubmitButtonWrapper isInIframe={isInIframe}>
        <SubmitButton
          data-element_id={ButtonId.All.ButtonNext}
          onClick={handleClickSubmit}
          $isEnabled={canSubmit}
          backgroundColor={
            isReverifyButton
              ? color.red
              : canSubmit
                ? color.sky
                : color.neutral['10_alpha_80']
          }
          borderColor={
            isReverifyButton
              ? color.red
              : canSubmit
                ? color.sky
                : 'rgba(71, 71, 71, 0.8)'
          }
          color={
            isReverifyButton
              ? color.neutral[100]
              : canSubmit
                ? color.neutral[0]
                : 'rgba(255, 255, 255, 0.2)'
          }
          disabled={!canSubmit}
        >
          {isLoading ? (
            <Loading size={22} />
          ) : isGotItButton ? (
            t('i_got_it', {
              ns: TranslationNamespace.CREATOR_ONBOARDING,
            })
          ) : isReverifyButton ? (
            t('re_verify', {
              ns: TranslationNamespace.CREATOR_ONBOARDING,
            })
          ) : isFinishButton ? (
            t('general_done', {
              ns: TranslationNamespace.GENERAL,
            })
          ) : (
            t('continue', {
              ns: TranslationNamespace.GENERAL,
            })
          )}
        </SubmitButton>
      </SubmitButtonWrapper>
    );
  };
  const renderForm = () => {
    let Component = (
      <LoadingWrapper>
        <Loading size={32} />
      </LoadingWrapper>
    );
    if (!isServer) {
      switch (progress) {
        case JoinProgress.PERSONAL_INFORMATION:
          Component = (
            <UniversalComponent
              filename="Container_JoinPersonalInformationForm"
              progress={progress}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/JoinPersonalInformationForm.js')
              }
            />
          );
          break;
        case JoinProgress.UPLOAD_IDENTIFICATION:
          Component = (
            <UniversalComponent
              filename="Container_JoinUploadIdentificationForm"
              isJoinCreator
              progress={progress}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/JoinUploadIdentificationForm.js')
              }
            />
          );
          break;
        case JoinProgress.OCR_PROCESSING:
          Component = (
            <UniversalComponent
              filename="Container_JoinOcrProcessing"
              isJoinCreator
              progress={progress}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/JoinOcrProcessing.js')
              }
            />
          );
          break;
        case JoinProgress.OCR_RESULT:
          Component = (
            <UniversalComponent
              filename="Container_JoinOcrResult"
              progress={progress}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/JoinOcrResult.js')
              }
            />
          );
          break;
        case JoinProgress.MANUAL_REVIEW:
          Component = (
            <UniversalComponent
              filename="Component_JoinManualReview"
              progress={progress}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../component/JoinManualReview.jsx')
              }
            />
          );
          break;
        default:
          Component = (
            <LoadingWrapper>
              <Loading size={32} />
            </LoadingWrapper>
          );
      }
    }
    return <FormWrapper>{Component}</FormWrapper>;
  };

  useEffect(() => {
    nextTick.current = setTimeout(() => {
      setIsServer(false);
      fetchMeCreatorRef.current();
    }, 0);

    return () => {
      clearTimeout(nextTick.current);
    };
  }, []);

  useEffect(() => {
    if (previousMeId.current === meId) {
      return;
    }
    previousMeId.current = meId;
    fetchMeCreator();
  }, [fetchMeCreator, meId]);

  const shouldPadFooter = !isServer && isIphoneXSeriesPWA;
  const shouldShowHeader = !isXUserAgent;
  const shouldShowCloseButton = !isInIframe;
  const isPersonalInformation = JoinProgress.PERSONAL_INFORMATION === progress;

  return (
    <StyledJoin>
      {isPersonalInformation && (
        <CaptchaWrapper onSubmit={() => proceedJoinStep({ progress })}>
          {({ startVerification, CaptchaWidget }) => {
            startCaptchaVerification.current = startVerification;
            return CaptchaWidget;
          }}
        </CaptchaWrapper>
      )}
      {shouldShowHeader && (
        <Header>
          <Title>
            {t('onboarding', {
              ns: TranslationNamespace.CREATOR_ONBOARDING,
            })}
          </Title>
          {shouldShowCloseButton && (
            <HeaderAction
              onClick={handleClickClose}
              data-element_id={ButtonId.All.ButtonClose}
            >
              <img
                src={CloseIconSource}
                alt={t('general_cancel', {
                  ns: TranslationNamespace.GENERAL,
                })}
              />
            </HeaderAction>
          )}
        </Header>
      )}
      <Body
        shouldPadFooter={shouldPadFooter}
        shouldShowHeader={shouldShowHeader}
        deviceHeight={deviceHeight}
      >
        {renderForm()}
        {renderSubmitButton()}
        {isPersonalInformation && !meId && (
          <ConsentTermsWrapper>
            {t('agree_tos', {
              ns: TranslationNamespace.CREATOR_ONBOARDING,
            })}
            <ConsentTermsLink
              to="/tos"
              data-element_id={ButtonId.Setting.ButtonTos}
            >
              {t('terms', {
                ns: TranslationNamespace.CREATOR_ONBOARDING,
              })}
            </ConsentTermsLink>
          </ConsentTermsWrapper>
        )}
        {JoinProgress.UPLOAD_IDENTIFICATION === progress && (
          <TermsWrapper>
            {t('upload_identification_notice', {
              ns: TranslationNamespace.CREATOR_ONBOARDING,
            })}
            <TermsLink to="/tos">
              {t('terms', {
                ns: TranslationNamespace.CREATOR_ONBOARDING,
              })}
            </TermsLink>
            <PoweredBy>
              © Powered By
              <img src={ShuftiLogoSource} alt="Shufti Pro" />
            </PoweredBy>
          </TermsWrapper>
        )}
      </Body>
      <ModalView
        modalId={ModalId.Join.ModalVerificationNotComplete}
        id="LeaveJoinConfirmModal"
        render={() => (
          <UniversalComponent
            filename="Container_LeaveJoinConfirmModal"
            isJoinCreator
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LeaveJoinConfirmModal.js')
            }
          />
        )}
      />
      <ModalView
        id="JoinCountrySelect"
        render={() => (
          <UniversalComponent
            filename="Container_JoinCountrySelect"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/JoinCountrySelect.js')
            }
          />
        )}
      />
      <ModalView
        modalId={ModalId.Join.ModalMaleReject}
        id="JoinGenderRejectModal"
        render={() => (
          <UniversalComponent
            filename="Container_JoinGenderRejectModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/JoinGenderRejectModal.js')
            }
          />
        )}
      />
      <ModalView
        modalId={ModalId.Join.ModalPhotoRequirement}
        id="JoinPhotoRequirementsModal"
      >
        <UniversalComponent
          filename="Container_JoinPhotoRequirementsModal"
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/JoinPhotoRequirementsModal.js')
          }
        />
      </ModalView>
    </StyledJoin>
  );
});

Join.propTypes = {
  meId: PropTypes.string,
  progress: PropTypes.string,
  kycStatus: PropTypes.string,
  deviceHeight: PropTypes.number,
  canSubmit: PropTypes.bool,
  isSubmitMeKycFetching: PropTypes.bool,
  isSubmitMeCreatorFetching: PropTypes.bool,
  isFetchMeCreatorFetching: PropTypes.bool,
  isSubmitPasswordLoginFetching: PropTypes.bool,
  isMeAccountLinksFetching: PropTypes.bool,
  isEmailBindFetching: PropTypes.bool,
  isEmailBindOtpFetching: PropTypes.bool,
  addModal: PropTypes.func,
  proceedJoinStep: PropTypes.func,
  fetchMeCreator: PropTypes.func,
  goBack: PropTypes.func,
};

const StyledJoin = styled.div``;

const Header = styled.div`
  position: fixed;
  z-index: ${componentZIndex.header};
  padding: 0 16px;
  width: 100%;
  height: ${headerHeight}px;
  top: var(--space-to-pinned-notification);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${color.black};
  border-bottom: 1px solid ${color.neutral['30_alpha_50']};
`;

const HeaderAction = styled.button`
  ${ResetButtonStyle};
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  :first-child {
    margin-right: 12px;
  }

  :last-child {
    margin-left: 12px;
  }
`;

const Title = styled.div`
  ${TextEllipsis};
  flex: auto;
  font-size: 22px;
  font-weight: 600;
  color: ${textColor.white};
`;

const Body = styled.div.attrs(
  ({ shouldPadFooter, shouldShowHeader, deviceHeight }) => ({
    style: {
      paddingBottom: `${
        shouldPadFooter ? iPhoneXSeriesFooterPaddingInPWA : 32
      }px`,
      paddingTop: `calc(${shouldShowHeader ? headerHeight : 0}px + var(--space-to-pinned-notification))`,
      minHeight: deviceHeight ? `${deviceHeight}px` : '100vh',
    },
  })
)`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${color.black};
`;

const FormWrapper = styled.div`
  width: 100%;
  flex: auto;
  ${({ shouldCenter }) => {
    if (shouldCenter) {
      return css`
        display: flex;
        align-items: center;
        justify-content: center;
      `;
    }
  }}
`;

const LoadingWrapper = styled.div`
  margin-top: 206px;
  display: flex;
  justify-content: center;
`;

const SubmitButtonWrapper = styled.div`
  padding: 0 16px;
  width: 100%;
  max-width: 480px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ isInIframe }) =>
    isInIframe &&
    css`
      position: fixed;
      bottom: 16px;
    `}
`;

const SubmitButton = styled.button.attrs(
  ({ backgroundColor, borderColor, color }) => ({
    style: {
      backgroundColor,
      borderColor,
      color,
    },
  })
)`
  ${ResetButtonStyle};
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
  width: 100%;
  height: ${SUBMIT_BUTTON_HEIGHT}px;
  font-size: 18px;
  font-weight: 600;
  cursor: ${({ $isEnabled }) => ($isEnabled ? 'pointer' : 'default')};
`;

const TermsWrapper = styled.div`
  margin-top: 12px;
  padding-left: 16px;
  padding-right: 16px;
  max-width: 480px;
  font-size: 12px;
  line-height: 1.5;
  color: ${textColor.grey};
`;

const TermsLink = styled(Link)`
  margin-left: 4px;
  text-decoration: underline;
`;

const ConsentTermsWrapper = styled.div`
  margin-top: 16px;
  padding-left: 16px;
  padding-right: 16px;
  max-width: 480px;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.1px;
  text-align: center;
  color: ${color.neutral[75]};
`;

const ConsentTermsLink = styled(Link)`
  margin-left: 4px;
  font-weight: 600;
  text-decoration: underline;
  color: ${color.neutral[100]};
`;

const PoweredBy = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${textColor.black};

  > img {
    margin-left: 4px;
    height: 16px;
  }
`;

export default Join;
